import type { Permission } from '@cqinv/shared';
import type { SesionMe } from '../features/session/api';
import { puede } from '../lib/permisos';
import { pathDe } from '../lib/ruta';
import { textos } from '../textos';

export type IconoNav =
  | 'inicio'
  | 'escanear'
  | 'operaciones'
  | 'alquileres'
  | 'mantenimiento'
  | 'avisos'
  | 'activos'
  | 'qr'
  | 'maestros'
  | 'reportes'
  | 'importacion'
  | 'marca'
  | 'almacenamiento'
  | 'auditoria'
  | 'preferencias'
  | 'operar'
  | 'inventario'
  | 'administracion';

export type GrupoNav = {
  readonly id: 'operar' | 'inventario' | 'administracion';
  readonly titulo: string;
  readonly icono: IconoNav;
  readonly items: readonly DestinoNav[];
};

export type DestinoNav = {
  readonly href: string;
  readonly etiqueta: string;
  readonly icono?: IconoNav;
  readonly permiso?: Permission;
  readonly soloEscritorio?: boolean;
  readonly soloPlataforma?: boolean;
};

export const NAV_MOVIL_FIJO: readonly DestinoNav[] = [
  { href: '/', etiqueta: textos.nav.inicio },
  { href: '/activos', etiqueta: textos.nav.activos },
  { href: '/operaciones', etiqueta: textos.nav.operaciones },
];

export function destinosMas(sesion: SesionMe): readonly DestinoNav[] {
  const extra: DestinoNav[] = [
    { href: '/alquileres', etiqueta: textos.nav.alquileres },
    { href: '/mantenimiento', etiqueta: textos.nav.mantenimiento },
    { href: '/avisos', etiqueta: textos.nav.avisos },
    { href: '/qr', etiqueta: textos.qr.lotes },
    { href: '/maestros', etiqueta: textos.nav.maestros, soloEscritorio: true },
  ];
  if (puede(sesion, 'report.read')) {
    extra.push({ href: '/reportes', etiqueta: textos.reportes.titulo, permiso: 'report.read', soloEscritorio: true });
  }
  if (puede(sesion, 'import.run')) {
    extra.push({ href: '/importar', etiqueta: textos.nav.importacion, permiso: 'import.run', soloEscritorio: true });
  }
  if (puede(sesion, 'branding.configure')) {
    extra.push({ href: '/marca', etiqueta: textos.nav.marca, permiso: 'branding.configure', soloEscritorio: true });
  }
  if (puede(sesion, 'storage.configure')) {
    extra.push({
      href: '/almacenamiento',
      etiqueta: textos.nav.almacenamiento,
      permiso: 'storage.configure',
      soloEscritorio: true,
    });
  }
  if (puede(sesion, 'audit.read')) {
    extra.push({ href: '/auditoria', etiqueta: textos.nav.auditoria, permiso: 'audit.read' });
  }
  extra.push({ href: '/preferencias', etiqueta: textos.nav.preferencias });
  return extra;
}

/** Grupos del menú de la empresa. La gestión de clientes no vive aquí: es /plataforma. */
export const NAV_ESCRITORIO: readonly GrupoNav[] = [
  {
    id: 'operar',
    titulo: textos.nav.operar,
    icono: 'operar',
    items: [
      { href: '/', etiqueta: textos.nav.inicio, icono: 'inicio' },
      { href: '/escanear', etiqueta: textos.acciones.escanear, icono: 'escanear' },
      { href: '/operaciones', etiqueta: textos.nav.operaciones, icono: 'operaciones' },
      { href: '/alquileres', etiqueta: textos.nav.alquileres, icono: 'alquileres' },
      { href: '/mantenimiento', etiqueta: textos.nav.mantenimiento, icono: 'mantenimiento' },
      { href: '/avisos', etiqueta: textos.nav.avisos, icono: 'avisos' },
    ],
  },
  {
    id: 'inventario',
    titulo: textos.nav.inventario,
    icono: 'inventario',
    items: [
      { href: '/activos', etiqueta: textos.nav.activos, icono: 'activos' },
      { href: '/qr', etiqueta: textos.qr.lotes, icono: 'qr' },
      { href: '/maestros', etiqueta: textos.nav.maestros, icono: 'maestros' },
    ],
  },
  {
    id: 'administracion',
    titulo: textos.nav.administracion,
    icono: 'administracion',
    items: [
      { href: '/reportes', etiqueta: textos.reportes.titulo, icono: 'reportes', permiso: 'report.read' },
      { href: '/importar', etiqueta: textos.nav.importacion, icono: 'importacion', permiso: 'import.run' },
      { href: '/marca', etiqueta: textos.nav.marca, icono: 'marca', permiso: 'branding.configure' },
      { href: '/almacenamiento', etiqueta: textos.nav.almacenamiento, icono: 'almacenamiento', permiso: 'storage.configure' },
      { href: '/auditoria', etiqueta: textos.nav.auditoria, icono: 'auditoria', permiso: 'audit.read' },
      { href: '/preferencias', etiqueta: textos.nav.preferencias, icono: 'preferencias' },
    ],
  },
];

export function visibleEnNav(sesion: SesionMe, item: DestinoNav): boolean {
  if (item.soloPlataforma && !sesion.platformAdmin) return false;
  return item.permiso ? puede(sesion, item.permiso) : true;
}

export function rutaActiva(ruta: string, href: string): boolean {
  const camino = pathDe(ruta);
  if (href === '/') return camino === '/' || camino.startsWith('/tablero');
  return camino === href || camino.startsWith(`${href}/`);
}

export function grupoDeRuta(ruta: string): GrupoNav['id'] | null {
  for (const grupo of NAV_ESCRITORIO) {
    if (grupo.items.some((item) => rutaActiva(ruta, item.href))) return grupo.id;
  }
  return null;
}

export function grupoAbierto(id: string, grupos: Readonly<Record<string, boolean>>): boolean {
  return grupos[id] !== false;
}
